import { Position } from 'reactflow';
import { useCallback, useMemo } from 'react';
import { InputId, OutputId, NodeType } from 'web';
import { HandleType } from '@/enums/handle-type.enum';
import { useFlowView } from '@/components/Recipe/FlowContext';
import { useConnectionContext } from '@/components/Recipe/FlowComponents/FlowTour/ConnectionContext';
import { getHandleId } from '../Utils';
import DynamicHandleLabel from './DynamicHandleLabel';
import { CollapsedInputHandles } from './CollapsedInputHandles';
import { HANDLE_SPACING_PX, HANDLE_TOP_PX } from './handleCollapse';
import { useHandleCollapse } from './useHandleCollapse';
import { getInputHandleUI, getOutputHandleUI, NodeParameterUI } from './nodeUI';
import type { BaseNodeData, Handle, HandleSide } from '@/types/node';

const HANDLE_TOP = `${HANDLE_TOP_PX}px`;
const HANDLE_SPACING = HANDLE_SPACING_PX;

function getInputHandle(nodeType: NodeType | undefined, key: InputId): NodeParameterUI | undefined {
  return nodeType ? getInputHandleUI(nodeType, key) : undefined;
}
export function getOutputHandle(nodeType: NodeType | undefined, key: OutputId): NodeParameterUI | undefined {
  return nodeType ? getOutputHandleUI(nodeType, key) : undefined;
}

/** Resolves the color/type shown for an input handle before and after a connection is made. */
export function resolveInputHandleType(
  inputType: HandleType | undefined,
  validInputTypes: HandleType[],
  fallbackType: HandleType | undefined,
): HandleType {
  if (inputType) {
    return inputType;
  }

  if (validInputTypes.length > 1) {
    const hasImageType = validInputTypes.includes(HandleType.Image);
    const hasVideoType = validInputTypes.includes(HandleType.Video);
    const hasAudioType = validInputTypes.includes(HandleType.Audio);
    const hasKlingElementType = validInputTypes.includes(HandleType.KlingElement);
    if (hasAudioType) {
      return HandleType.Any;
    }
    if (hasImageType !== hasVideoType && !hasKlingElementType) {
      if (fallbackType === HandleType.Mask) {
        // TODO: how can we handle mask input? fallbackType is deprecated data
        return HandleType.Mask;
      }
      return hasImageType ? HandleType.Image : HandleType.Video;
    }
    return HandleType.Any;
  }
  return validInputTypes[0] ?? fallbackType;
}

interface DynamicNodeHandlesProps {
  handleColor?: string;
  handles: BaseNodeData['handles'];
  id: string;
  inputHandleYPos?: string;
  isHovered: boolean;
  isSelected?: boolean;
  nodeType?: string;
  outputHandleYPos?: string;
  shouldObscureHandle?: (handleType: HandleType, handleId: string, handleSide: HandleSide) => boolean;
  showFullNode: boolean;
  sortedInputHandles: [string, Handle][];
  sortedOutputHandles: [string, Handle][];
  invalidHandles?: {
    required?: string[];
    requiredEmpty?: string[];
  };
  version?: number;
  handleCollapse?: { expandedTypes: HandleType[] };
}

export const DynamicNodeHandles = ({
  handleColor,
  handles,
  id,
  inputHandleYPos,
  isHovered,
  isSelected,
  nodeType,
  outputHandleYPos,
  shouldObscureHandle,
  showFullNode,
  sortedInputHandles,
  sortedOutputHandles,
  invalidHandles,
  version,
  handleCollapse,
}: DynamicNodeHandlesProps) => {
  const flowView = useFlowView();
  const { currentConnection } = useConnectionContext();

  /// highlight relevant handles when dragging a new edge
  const shouldShowHandleLabel = useCallback(
    (handleType: HandleType, handleId: string, nodeId: string, handleSide: HandleSide) => {
      // while connecting, if opposite side, show handle
      if (currentConnection && currentConnection.handleSide !== handleSide) {
        return true;
      }
      // if selected / hovered or itself the handle, show handle
      return isSelected || currentConnection?.handleId === handleId || isHovered;
    },
    [currentConnection, isHovered, isSelected],
  );

  const commonHandleProps = useMemo(
    () => ({
      isSelectedNode: showFullNode,
      nodeId: id,
      shouldObscureHandle,
      shouldShowHandleLabel,
      handleColor,
      handleSpace: HANDLE_SPACING,
    }),
    [showFullNode, id, shouldObscureHandle, shouldShowHandleLabel, handleColor],
  );

  const getInputLabel = useCallback(
    (key: string, fallbackLabel?: string): string => {
      const dynamicName = flowView.getInputHandleName(id, key);
      if (dynamicName !== undefined) return dynamicName;
      return getInputHandle(nodeType as NodeType, key)?.label ?? fallbackLabel ?? key;
    },
    [flowView, id, nodeType],
  );

  const getHandleType = useCallback(
    (key: string, fallbackType: HandleType | undefined) =>
      resolveInputHandleType(flowView.nodeInputType(id, key), flowView.nodeValidInputTypes(id, key), fallbackType),
    [flowView, id],
  );

  const { inputRows, isCollapseFeatureEnabled, isGroupExpanded, groupHasError, toggleGroup } = useHandleCollapse({
    nodeId: id,
    isSelected,
    version,
    sortedInputHandles,
    resolveType: getHandleType,
    handleCollapse,
    invalidHandles,
  });

  /**
   * With the flag on, always render through `CollapsedInputHandles` — mode off is simply "every
   * type expanded" (`resolveHandleRows` returns sequential, uncollapsed rows for it). This keeps a
   * member handle's DOM node mounted under the same `row.key` across a mode toggle, which is what
   * lets it animate `top`/`opacity` instead of being unmounted and remounted, and keeps
   * `useRowRemeasure` (keyed on the same rows) firing on the toggle so React Flow's edge geometry
   * doesn't go stale.
   *
   * Flag off keeps rendering the flat list untouched, exactly as before this feature existed.
   */
  const renderInputHandles = () => {
    if (isCollapseFeatureEnabled) {
      return (
        <CollapsedInputHandles
          rows={inputRows}
          nodeId={id}
          nodeType={nodeType}
          handleTop={inputHandleYPos || HANDLE_TOP}
          invalidHandles={invalidHandles}
          getDescription={(key, fallback) => getInputHandle(nodeType as NodeType, key)?.description ?? fallback}
          getLabel={getInputLabel}
          getType={getHandleType}
          isGroupExpanded={isGroupExpanded}
          groupHasError={groupHasError}
          onToggleGroup={toggleGroup}
          isConnecting={Boolean(currentConnection)}
          showGroupLabels={Boolean(isHovered || isSelected)}
          commonHandleProps={commonHandleProps}
        />
      );
    }

    return sortedInputHandles.map(([key, inputHandle], index) => (
      <DynamicHandleLabel
        key={key}
        description={getInputHandle(nodeType as NodeType, key)?.description ?? inputHandle.description}
        format={inputHandle.format}
        handleId={getHandleId(id, 'input', key)} //{`${id}-input-${key}`}
        handlePosition={Position.Left}
        handleSide="target"
        handleTop={inputHandleYPos || HANDLE_TOP}
        handleType={getHandleType(key, inputHandle.type)}
        index={index}
        label={getInputLabel(key, inputHandle.label)}
        required={inputHandle.required}
        validationErrors={{
          required: invalidHandles?.required?.includes(key),
          requiredEmpty: invalidHandles?.requiredEmpty?.includes(key),
        }}
        {...commonHandleProps}
      />
    ));
  };

  return (
    <>
      {version === 2 || version === 3
        ? renderInputHandles()
        : (Array.isArray(handles?.input) ? handles.input : null)?.map((inputHandle, index) => (
            <DynamicHandleLabel
              key={index}
              handleSide="target"
              handlePosition={Position.Left}
              handleTop={inputHandleYPos || HANDLE_TOP}
              handleId={getHandleId(id, 'input', inputHandle)} //{`${id}-input-${inputHandle}`}
              label={inputHandle}
              index={index}
              {...commonHandleProps}
            />
          ))}
      {typeof handles?.output === 'object' && !Array.isArray(handles?.output)
        ? sortedOutputHandles.map(([key, outputHandle], index) => (
            <DynamicHandleLabel
              key={index}
              handleSide="source"
              handlePosition={Position.Right}
              handleTop={outputHandleYPos || HANDLE_TOP}
              handleId={getHandleId(id, 'output', key)} //{`${id}-output-${outputHandle}`}
              label={getOutputHandle(nodeType as NodeType, key)?.label ?? outputHandle.label ?? key}
              index={index}
              description={getOutputHandle(nodeType as NodeType, key)?.description ?? outputHandle.description}
              format={outputHandle.format}
              handleType={flowView.nodeOutputType(id, key) ?? outputHandle.type}
              {...commonHandleProps}
            />
          ))
        : (Array.isArray(handles?.output) ? handles.output : null)?.map((outputHandle, index) => (
            <DynamicHandleLabel
              key={index}
              handleSide="source"
              handlePosition={Position.Right}
              handleTop={outputHandleYPos || HANDLE_TOP}
              handleId={getHandleId(id, 'output', outputHandle)} //{`${id}-output-${outputHandle}`}
              label={outputHandle}
              index={index}
              handleType={flowView.nodeOutputType(id, outputHandle) ?? HandleType.Any}
              {...commonHandleProps}
            />
          ))}
    </>
  );
};
